import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { format, subDays, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { id } from 'date-fns/locale';
import supabase from '../../lib/supabase';
import ReactECharts from 'echarts-for-react';

const {
  FiBarChart2,
  FiCalendar,
  FiClock,
  FiDownload,
  FiMessageSquare,
  FiRefreshCw,
  FiAlertCircle,
  FiUser,
  FiSun,
  FiMoon,
  FiX,
  FiTarget,
  FiTrendingUp,
  FiThermometer,
  FiCheck,
  FiUsers,
  FiPhone
} = FiIcons;

const AnalyticsDashboard = () => {
  const [timeRange, setTimeRange] = useState('week');
  const [customDateRange, setCustomDateRange] = useState(false);
  const [startDateCustom, setStartDateCustom] = useState('');
  const [endDateCustom, setEndDateCustom] = useState('');
  const [metrics, setMetrics] = useState({
    totalConversations: 0,
    uniqueWhatsappNumbers: 0,
    leadStatusBreakdown: {},
    timeDistribution: {
      morning: 0, // 6-12
      afternoon: 0, // 12-18
      evening: 0, // 18-24
      night: 0, // 0-6
    }
  });
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hourlyDistribution, setHourlyDistribution] = useState([]);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);

  const getDateRange = () => {
    if (customDateRange && startDateCustom && endDateCustom) {
      return {
        start: startDateCustom + 'T00:00:00',
        end: endDateCustom + 'T23:59:59',
        label: `${format(new Date(startDateCustom), 'dd MMM yyyy', { locale: id })} - ${format(new Date(endDateCustom), 'dd MMM yyyy', { locale: id })}`
      };
    }

    const now = new Date();
    switch (timeRange) {
      case 'day':
        return {
          start: startOfDay(now).toISOString(),
          end: endOfDay(now).toISOString(),
          label: format(now, 'dd MMMM yyyy', { locale: id })
        };
      case 'week':
        return {
          start: startOfWeek(now, { weekStartsOn: 1 }).toISOString(),
          end: endOfWeek(now, { weekStartsOn: 1 }).toISOString(),
          label: `${format(startOfWeek(now, { weekStartsOn: 1 }), 'dd MMM', { locale: id })} - ${format(endOfWeek(now, { weekStartsOn: 1 }), 'dd MMM yyyy', { locale: id })}`
        };
      case 'month':
        return {
          start: startOfMonth(now).toISOString(),
          end: endOfMonth(now).toISOString(),
          label: format(now, 'MMMM yyyy', { locale: id })
        };
      case '30days':
        return {
          start: startOfDay(subDays(now, 30)).toISOString(),
          end: endOfDay(now).toISOString(),
          label: `${format(subDays(now, 30), 'dd MMM', { locale: id })} - ${format(now, 'dd MMM yyyy', { locale: id })}`
        };
      default:
        return {
          start: startOfWeek(now, { weekStartsOn: 1 }).toISOString(),
          end: endOfWeek(now, { weekStartsOn: 1 }).toISOString(),
          label: `${format(startOfWeek(now, { weekStartsOn: 1 }), 'dd MMM', { locale: id })} - ${format(endOfWeek(now, { weekStartsOn: 1 }), 'dd MMM yyyy', { locale: id })}`
        };
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const { start, end } = getDateRange();

      // Get total conversations and unique WhatsApp numbers
      const { data: conversationData, error: conversationError } = await supabase
        .from('log_percakapan_urology_expert')
        .select('id_whatsapp,waktu_pesan,jenis_pesan')
        .gte('waktu_pesan', start)
        .lte('waktu_pesan', end);

      if (conversationError) throw conversationError;

      // Process data for metrics
      const uniqueNumbers = new Set();
      const timeDistribution = {
        morning: 0, // 6-12
        afternoon: 0, // 12-18
        evening: 0, // 18-24
        night: 0, // 0-6
      };

      // Initialize hourly distribution
      const hourlyDist = Array(24).fill(0);

      // Only count incoming messages for time distribution
      const incomingMessages = conversationData.filter(msg => msg.jenis_pesan !== 'keluar');

      incomingMessages.forEach(msg => {
        uniqueNumbers.add(msg.id_whatsapp);

        // Calculate time distribution
        try {
          const msgDate = new Date(msg.waktu_pesan);
          const hour = msgDate.getHours();

          // Increment the appropriate time period count
          if (hour >= 6 && hour < 12) {
            timeDistribution.morning++;
          } else if (hour >= 12 && hour < 18) {
            timeDistribution.afternoon++;
          } else if (hour >= 18 && hour < 24) {
            timeDistribution.evening++;
          } else {
            timeDistribution.night++;
          }

          // Increment hourly distribution
          hourlyDist[hour]++;
        } catch (e) {
          console.error('Error parsing date:', e);
        }
      });

      // Get unique WhatsApp numbers array
      const uniqueWhatsappArray = Array.from(uniqueNumbers);
      setWhatsappNumbers(uniqueWhatsappArray);

      // Fetch lead status breakdown from CRM
      let leadStatusBreakdown = {};
      try {
        const { data: crmData, error: crmError } = await supabase
          .from('customer_data_crm')
          .select('id_whatsapp,lead_status');

        if (crmError && crmError.code !== 'PGRST116') {
          throw crmError;
        }

        if (crmData) {
          // Create mapping of WhatsApp numbers to lead status
          const crmMap = {};
          crmData.forEach(customer => {
            crmMap[customer.id_whatsapp] = customer.lead_status || 'cold';
          });

          // Count lead status for all unique numbers (default to 'cold' if not in CRM)
          uniqueWhatsappArray.forEach(whatsappNumber => {
            const leadStatus = crmMap[whatsappNumber] || 'cold';
            leadStatusBreakdown[leadStatus] = (leadStatusBreakdown[leadStatus] || 0) + 1;
          });
        } else {
          // If no CRM data, all are cold leads
          leadStatusBreakdown = { cold: uniqueWhatsappArray.length };
        }
      } catch (crmError) {
        console.log('CRM data not available, defaulting to cold leads');
        leadStatusBreakdown = { cold: uniqueWhatsappArray.length };
      }

      // Update metrics
      setMetrics({
        totalConversations: conversationData.length,
        uniqueWhatsappNumbers: uniqueNumbers.size,
        leadStatusBreakdown: leadStatusBreakdown,
        timeDistribution: timeDistribution
      });

      // Set hourly distribution for chart
      setHourlyDistribution(hourlyDist);

      // Fetch data for chart
      const { data: chartData, error: chartError } = await supabase
        .from('log_percakapan_urology_expert')
        .select('waktu_pesan,jenis_pesan')
        .gte('waktu_pesan', start)
        .lte('waktu_pesan', end)
        .order('waktu_pesan', { ascending: true });

      if (chartError) throw chartError;

      // Process chart data
      const dateGroups = {};
      chartData.filter(item => item.jenis_pesan !== 'keluar').forEach(item => {
        const date = format(
          new Date(item.waktu_pesan),
          timeRange === 'day' ? 'HH:00' : 'yyyy-MM-dd',
          { locale: id }
        );
        dateGroups[date] = (dateGroups[date] || 0) + 1;
      });

      const chartSeries = Object.keys(dateGroups).map(date => ({
        date,
        count: dateGroups[date]
      }));

      setChartData(chartSeries);

    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Gagal mengambil data analitik: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when timeRange changes or when custom date range is applied
  useEffect(() => {
    fetchAnalytics();
  }, [timeRange, customDateRange, startDateCustom, endDateCustom]);

  // Function to apply custom date range
  const applyCustomDateRange = () => {
    if (startDateCustom && endDateCustom) {
      setCustomDateRange(true);
      setTimeRange('custom');
      setShowDateRangePicker(false);
    }
  };

  // Function to reset to predefined ranges
  const resetToPresetRange = (range) => {
    setCustomDateRange(false);
    setTimeRange(range);
  };

  // Function to export data to CSV
  const exportToCSV = () => {
    // Create data for export
    const { label } = getDateRange();
    const leadData = Object.entries(metrics.leadStatusBreakdown).map(([status, count]) => ({
      Status: status,
      Count: count
    }));

    // Convert status data to CSV
    let csvContent = "data:text/csv;charset=utf-8,";

    // Add header
    csvContent += `AI Agent Analytics Report - ${label}\r\n\r\n`;

    // Add metrics
    csvContent += `Total Messages,${metrics.totalConversations}\r\n`;
    csvContent += `Unique WhatsApp Numbers,${metrics.uniqueWhatsappNumbers}\r\n\r\n`;

    // Add time distribution
    csvContent += "Time Period,Message Count\r\n";
    csvContent += `Morning (6-12),${metrics.timeDistribution.morning}\r\n`;
    csvContent += `Afternoon (12-18),${metrics.timeDistribution.afternoon}\r\n`;
    csvContent += `Evening (18-24),${metrics.timeDistribution.evening}\r\n`;
    csvContent += `Night (0-6),${metrics.timeDistribution.night}\r\n\r\n`;

    // Add lead status breakdown
    csvContent += "Lead Status,Count\r\n";
    leadData.forEach(row => {
      csvContent += `${row.Status},${row.Count}\r\n`;
    });

    // Add WhatsApp numbers list
    csvContent += "\r\nWhatsApp Numbers\r\n";
    whatsappNumbers.forEach(number => {
      csvContent += `${number}\r\n`;
    });

    // Add chart data
    csvContent += "\r\nDate,Message Count\r\n";
    chartData.forEach(item => {
      csvContent += `${item.date},${item.count}\r\n`;
    });

    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `ai_agent_analytics_${label.replace(/\s/g, '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getLeadStatusIcon = (status) => {
    switch (status) {
      case 'cold': return FiThermometer;
      case 'warm': return FiTrendingUp;
      case 'hot': return FiTarget;
      case 'customer': return FiCheck;
      default: return FiThermometer;
    }
  };

  const getLeadStatusColor = (status) => {
    switch (status) {
      case 'cold': return 'text-blue-500 bg-blue-50 border-blue-200';
      case 'warm': return 'text-orange-500 bg-orange-50 border-orange-200';
      case 'hot': return 'text-red-500 bg-red-50 border-red-200';
      case 'customer': return 'text-green-500 bg-green-50 border-green-200';
      default: return 'text-gray-500 bg-gray-50 border-gray-200';
    }
  };

  const getChartOptions = () => {
    return {
      tooltip: {
        trigger: 'axis',
        formatter: function(params) {
          return `${params[0].name}<br/>${params[0].seriesName}: ${params[0].value}`;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: chartData.map(item => item.date),
        axisLabel: {
          rotate: timeRange !== 'day' ? 45 : 0,
          interval: timeRange === 'month' ? 'auto' : 0
        }
      },
      yAxis: {
        type: 'value',
        minInterval: 1
      },
      series: [
        {
          name: 'Messages',
          type: 'bar',
          data: chartData.map(item => item.count),
          itemStyle: {
            color: '#4f46e5'
          },
          emphasis: {
            itemStyle: {
              color: '#6366f1'
            }
          },
          barWidth: timeRange === 'month' ? '60%' : '80%'
        }
      ]
    };
  };

  // Get time distribution chart options
  const getTimeDistributionOptions = () => {
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'horizontal',
        bottom: 'bottom'
      },
      series: [
        {
          name: 'Time Distribution',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '18',
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: false
          },
          data: [
            {
              value: metrics.timeDistribution.morning,
              name: 'Morning (6-12)',
              itemStyle: { color: '#fbbf24' } // yellow/gold for morning
            },
            {
              value: metrics.timeDistribution.afternoon,
              name: 'Afternoon (12-18)',
              itemStyle: { color: '#fb923c' } // orange for afternoon
            },
            {
              value: metrics.timeDistribution.evening,
              name: 'Evening (18-24)',
              itemStyle: { color: '#4f46e5' } // indigo for evening
            },
            {
              value: metrics.timeDistribution.night,
              name: 'Night (0-6)',
              itemStyle: { color: '#1e293b' } // dark blue/slate for night
            }
          ]
        }
      ]
    };
  };

  // Get hourly distribution chart options
  const getHourlyDistributionOptions = () => {
    return {
      tooltip: {
        trigger: 'axis',
        formatter: function(params) {
          return `${params[0].name}:00 - ${(parseInt(params[0].name) + 1) % 24}:00<br/>Messages: ${params[0].value}`;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: Array.from({ length: 24 }, (_, i) => i),
        axisLabel: {
          formatter: function(value) {
            return `${value}:00`;
          }
        }
      },
      yAxis: {
        type: 'value',
        minInterval: 1
      },
      series: [
        {
          name: 'Messages by Hour',
          type: 'bar',
          data: hourlyDistribution,
          itemStyle: {
            color: function(params) {
              const hour = params.dataIndex;
              if (hour >= 6 && hour < 12) return '#fbbf24'; // morning - yellow
              if (hour >= 12 && hour < 18) return '#fb923c'; // afternoon - orange
              if (hour >= 18 && hour < 24) return '#4f46e5'; // evening - indigo
              return '#1e293b'; // night - dark blue
            }
          },
          emphasis: {
            itemStyle: {
              color: function(params) {
                const hour = params.dataIndex;
                if (hour >= 6 && hour < 12) return '#f59e0b'; // darker yellow
                if (hour >= 12 && hour < 18) return '#ea580c'; // darker orange
                if (hour >= 18 && hour < 24) return '#3730a3'; // darker indigo
                return '#0f172a'; // darker blue
              }
            }
          }
        }
      ]
    };
  };

  // Get lead funnel chart options
  const getLeadFunnelOptions = () => {
    const funnelData = [
      { value: metrics.leadStatusBreakdown.cold || 0, name: 'Cold Leads' },
      { value: metrics.leadStatusBreakdown.warm || 0, name: 'Warm Leads' },
      { value: metrics.leadStatusBreakdown.hot || 0, name: 'Hot Leads' },
      { value: metrics.leadStatusBreakdown.customer || 0, name: 'Customers' }
    ].filter(item => item.value > 0); // Only show non-zero values

    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)'
      },
      series: [
        {
          name: 'Lead Funnel',
          type: 'funnel',
          left: '10%',
          top: 60,
          bottom: 60,
          width: '80%',
          min: 0,
          max: Math.max(...funnelData.map(item => item.value)),
          minSize: '0%',
          maxSize: '100%',
          sort: 'descending',
          gap: 2,
          label: {
            show: true,
            position: 'inside'
          },
          labelLine: {
            length: 10,
            lineStyle: {
              width: 1,
              type: 'solid'
            }
          },
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 1
          },
          emphasis: {
            label: {
              fontSize: 20
            }
          },
          data: funnelData.map((item, index) => ({
            ...item,
            itemStyle: {
              color: index === 0 ? '#3b82f6' : // blue for cold
                    index === 1 ? '#f97316' : // orange for warm
                    index === 2 ? '#ef4444' : // red for hot
                    '#10b981' // green for customers
            }
          }))
        }
      ]
    };
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <SafeIcon icon={FiBarChart2} className="text-white text-2xl" />
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-white">Analytics Dashboard</h1>
                <p className="text-indigo-100 text-sm">
                  {getDateRange().label}
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-3">
              <div className="flex space-x-1 bg-white/10 rounded-lg p-1">
                <button
                  onClick={() => resetToPresetRange('day')}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    timeRange === 'day' && !customDateRange ? 'bg-white text-indigo-700' : 'text-white hover:bg-white/10'
                  }`}
                >
                  Hari Ini
                </button>
                <button
                  onClick={() => resetToPresetRange('week')}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    timeRange === 'week' && !customDateRange ? 'bg-white text-indigo-700' : 'text-white hover:bg-white/10'
                  }`}
                >
                  Minggu Ini
                </button>
                <button
                  onClick={() => resetToPresetRange('month')}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    timeRange === 'month' && !customDateRange ? 'bg-white text-indigo-700' : 'text-white hover:bg-white/10'
                  }`}
                >
                  Bulan Ini
                </button>
                <button
                  onClick={() => resetToPresetRange('30days')}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    timeRange === '30days' && !customDateRange ? 'bg-white text-indigo-700' : 'text-white hover:bg-white/10'
                  }`}
                >
                  30 Hari
                </button>
                <button
                  onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    customDateRange ? 'bg-white text-indigo-700' : 'text-white hover:bg-white/10'
                  }`}
                >
                  Kustom
                </button>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={fetchAnalytics}
                  className="bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg transition-colors flex items-center space-x-1"
                >
                  <SafeIcon icon={FiRefreshCw} className="text-sm" />
                  <span className="text-sm">Refresh</span>
                </button>
                <button
                  onClick={exportToCSV}
                  disabled={loading || error}
                  className={`bg-white text-indigo-700 hover:bg-indigo-50 px-3 py-2 rounded-lg transition-colors flex items-center space-x-1 ${
                    loading || error ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <SafeIcon icon={FiDownload} className="text-sm" />
                  <span className="text-sm">Export CSV</span>
                </button>
              </div>
            </div>
          </div>

          {/* Custom Date Range Picker */}
          {showDateRangePicker && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 p-4 bg-white rounded-xl shadow-md"
            >
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-gray-800 font-medium">Pilih Rentang Tanggal</h3>
                <button
                  onClick={() => setShowDateRangePicker(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dari Tanggal</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={startDateCustom}
                    onChange={(e) => setStartDateCustom(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sampai Tanggal</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={endDateCustom}
                    onChange={(e) => setEndDateCustom(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end mt-4 space-x-3">
                <button
                  onClick={() => {
                    setStartDateCustom('');
                    setEndDateCustom('');
                    setCustomDateRange(false);
                    setShowDateRangePicker(false);
                    resetToPresetRange('week');
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Batal
                </button>
                <button
                  onClick={applyCustomDateRange}
                  disabled={!startDateCustom || !endDateCustom}
                  className={`px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center space-x-1 ${
                    !startDateCustom || !endDateCustom ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <SafeIcon icon={FiCalendar} className="text-sm" />
                  <span>Terapkan Filter</span>
                </button>
              </div>
            </motion.div>
          )}
        </div>

        {error ? (
          <div className="p-6 text-center">
            <div className="bg-red-50 p-4 rounded-xl border border-red-200 inline-block">
              <SafeIcon icon={FiAlertCircle} className="text-red-500 text-3xl mx-auto mb-2" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        ) : loading ? (
          <div className="p-6 flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="p-6">
            {/* Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
              >
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <SafeIcon icon={FiMessageSquare} className="text-blue-500 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Total Pesan</h3>
                    <p className="text-2xl font-bold text-gray-800">{metrics.totalConversations}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
              >
                <div className="flex items-center space-x-3">
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <SafeIcon icon={FiPhone} className="text-purple-500 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Jumlah Nomor WhatsApp</h3>
                    <p className="text-2xl font-bold text-gray-800">{metrics.uniqueWhatsappNumbers}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {whatsappNumbers.length > 0 && (
                        <span>
                          Total: {whatsappNumbers.slice(0, 3).join(', ')}
                          {whatsappNumbers.length > 3 && ` +${whatsappNumbers.length - 3} lainnya`}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Lead Status Funnel */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
              <h2 className="text-lg font-semibold mb-4">Lead Status Funnel</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Funnel chart */}
                <div className="h-80">
                  <ReactECharts
                    option={getLeadFunnelOptions()}
                    style={{ height: '100%', width: '100%' }}
                    opts={{ renderer: 'svg' }}
                  />
                </div>
                {/* Lead status cards */}
                <div className="grid grid-cols-1 gap-3">
                  {Object.entries(metrics.leadStatusBreakdown).map(([status, count], index) => (
                    <motion.div
                      key={status}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className={`border rounded-xl p-4 ${getLeadStatusColor(status)}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <SafeIcon icon={getLeadStatusIcon(status)} className="text-lg" />
                          <span className="font-medium capitalize">
                            {status === 'cold' ? 'Cold Lead' :
                             status === 'warm' ? 'Warm Lead' :
                             status === 'hot' ? 'Hot Lead' :
                             status === 'customer' ? 'Customer' : status}
                          </span>
                        </div>
                        <div className="text-lg font-bold">
                          {count}
                        </div>
                      </div>
                      <div className="text-sm mt-1">
                        {metrics.uniqueWhatsappNumbers > 0 ? 
                          `${Math.round((count / metrics.uniqueWhatsappNumbers) * 100)}%` : '0%'
                        } dari total nomor
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Time Distribution Section */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
              <h2 className="text-lg font-semibold mb-4">Distribusi Waktu Pesan</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Pie chart */}
                <div className="h-80">
                  <ReactECharts
                    option={getTimeDistributionOptions()}
                    style={{ height: '100%', width: '100%' }}
                    opts={{ renderer: 'svg' }}
                  />
                </div>
                {/* Time cards */}
                <div className="grid grid-cols-2 gap-3">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-yellow-50 border border-yellow-200 rounded-xl p-4"
                  >
                    <div className="flex flex-col items-center md:items-start">
                      <div className="flex items-center space-x-2 mb-2">
                        <SafeIcon icon={FiSun} className="text-yellow-500 text-lg" />
                        <h3 className="font-medium text-yellow-700">Pagi (6-12)</h3>
                      </div>
                      <p className="text-3xl font-bold text-yellow-800">{metrics.timeDistribution.morning}</p>
                      <p className="text-sm text-yellow-600 mt-1">
                        {metrics.totalConversations > 0 ? 
                          `${Math.round((metrics.timeDistribution.morning / metrics.totalConversations) * 100)}%` : '0%'
                        }
                      </p>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-orange-50 border border-orange-200 rounded-xl p-4"
                  >
                    <div className="flex flex-col items-center md:items-start">
                      <div className="flex items-center space-x-2 mb-2">
                        <SafeIcon icon={FiSun} className="text-orange-500 text-lg" />
                        <h3 className="font-medium text-orange-700">Siang (12-18)</h3>
                      </div>
                      <p className="text-3xl font-bold text-orange-800">{metrics.timeDistribution.afternoon}</p>
                      <p className="text-sm text-orange-600 mt-1">
                        {metrics.totalConversations > 0 ? 
                          `${Math.round((metrics.timeDistribution.afternoon / metrics.totalConversations) * 100)}%` : '0%'
                        }
                      </p>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-indigo-50 border border-indigo-200 rounded-xl p-4"
                  >
                    <div className="flex flex-col items-center md:items-start">
                      <div className="flex items-center space-x-2 mb-2">
                        <SafeIcon icon={FiMoon} className="text-indigo-500 text-lg" />
                        <h3 className="font-medium text-indigo-700">Sore/Malam (18-24)</h3>
                      </div>
                      <p className="text-3xl font-bold text-indigo-800">{metrics.timeDistribution.evening}</p>
                      <p className="text-sm text-indigo-600 mt-1">
                        {metrics.totalConversations > 0 ? 
                          `${Math.round((metrics.timeDistribution.evening / metrics.totalConversations) * 100)}%` : '0%'
                        }
                      </p>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-slate-50 border border-slate-200 rounded-xl p-4"
                  >
                    <div className="flex flex-col items-center md:items-start">
                      <div className="flex items-center space-x-2 mb-2">
                        <SafeIcon icon={FiMoon} className="text-slate-500 text-lg" />
                        <h3 className="font-medium text-slate-700">Dini Hari (0-6)</h3>
                      </div>
                      <p className="text-3xl font-bold text-slate-800">{metrics.timeDistribution.night}</p>
                      <p className="text-sm text-slate-600 mt-1">
                        {metrics.totalConversations > 0 ? 
                          `${Math.round((metrics.timeDistribution.night / metrics.totalConversations) * 100)}%` : '0%'
                        }
                      </p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Hourly Distribution Chart */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
              <h2 className="text-lg font-semibold mb-4">Distribusi Pesan per Jam</h2>
              <div className="h-80">
                <ReactECharts
                  option={getHourlyDistributionOptions()}
                  style={{ height: '100%', width: '100%' }}
                  opts={{ renderer: 'svg' }}
                />
              </div>
            </div>

            {/* Chart */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
              <h2 className="text-lg font-semibold mb-4">Tren Volume Pesan</h2>
              <div className="h-80">
                <ReactECharts
                  option={getChartOptions()}
                  style={{ height: '100%', width: '100%' }}
                  opts={{ renderer: 'svg' }}
                />
              </div>
            </div>

            {/* WhatsApp Numbers List */}
            {whatsappNumbers.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                <h2 className="text-lg font-semibold mb-4">Daftar Nomor WhatsApp ({whatsappNumbers.length})</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                  {whatsappNumbers.map((number, index) => (
                    <div key={index} className="text-sm bg-gray-50 p-2 rounded border">
                      {number}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;